import { Button } from "@package/subframe/components/Button";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import { FeatherArchive } from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useDeactivateTaskDefinitionCommandControllerDeactivate } from "@/generated/compliance-tasks/compliance-tasks";
import type { ComplianceTaskDefinitionResponseDto } from "@/generated/model/complianceTaskDefinitionResponseDto";
import {
  extractApiErrorMessage,
  invalidateComplianceQueries,
} from "@/lib/compliance/invalidate-compliance-queries";

interface DeactivateDefinitionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  definition: ComplianceTaskDefinitionResponseDto;
}

// Deactivation archives a compliance task: its history is kept but it is
// terminal (NAU-331) — there is no reactivation, so the copy makes that clear
// and steers the admin to create a new task instead.
export function DeactivateDefinitionDialog({
  open,
  onOpenChange,
  definition,
}: DeactivateDefinitionDialogProps) {
  const queryClient = useQueryClient();
  const deactivate = useDeactivateTaskDefinitionCommandControllerDeactivate();
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await deactivate.mutateAsync({ definitionId: definition.id });
      toast.success(`"${definition.name}" has been deactivated`);
      await invalidateComplianceQueries(queryClient);
      onOpenChange(false);
    } catch (error: unknown) {
      toast.error(
        extractApiErrorMessage(
          error,
          `Failed to deactivate "${definition.name}"`,
        ),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} className="max-w-[480px] p-0">
        <DialogTitle className="sr-only">
          Deactivate compliance task
        </DialogTitle>
        <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-white">
          <div className="flex max-w-[448px] flex-col items-center gap-6 p-4">
            <div className="flex flex-col items-center gap-6">
              <IconWithLogoBackground
                variant="warning"
                size="x-large"
                icon={<FeatherArchive />}
              />
              <div className="flex flex-col items-center gap-2">
                <span className="w-full text-center text-heading-2 font-heading-2 text-default-font">
                  Deactivate task?
                </span>
                <span className="text-center text-body font-body text-subtext-color">
                  Are you sure you want to deactivate{" "}
                  <strong>{definition.name}</strong>? Its history is kept, but
                  any open instance will be cancelled and no new instances will
                  be scheduled. This can&apos;t be undone — to run this check
                  again you&apos;d need to create a new compliance task.
                </span>
              </div>
            </div>
          </div>
          <div className="flex w-full flex-col items-center gap-3 bg-white px-4 py-4">
            <Button
              className="h-14 w-80 flex-none"
              variant="neutral-secondary"
              size="extra-large"
              onClick={() => onOpenChange(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              className="h-14 w-80 flex-none"
              variant="neutral-primary"
              size="extra-large"
              onClick={handleConfirm}
              disabled={busy}
            >
              {busy ? "Deactivating..." : "Yes, deactivate"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
